import React from "react";
import {
  Drawer as MuiDrawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export type DrawerPosition = "bottom" | "left" | "right";

export interface DrawerItemData {
  id: string;
  label: string;
  icon: React.ReactNode;
  isRunning?: boolean;
  onClick?: () => void;
}

interface DrawerProps {
  items: DrawerItemData[];
  position?: DrawerPosition;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  onReorder?: (items: DrawerItemData[]) => void;
}

// Sortable drawer item sub-component
const SortableDrawerItem: React.FC<{
  item: DrawerItemData;
  onClose: () => void;
}> = ({ item, onClose }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <ListItem ref={setNodeRef} style={style} disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        {...attributes}
        {...listeners}
        onClick={() => {
          item.onClick?.();
          onClose();
        }}
        sx={{
          borderRadius: "8px",
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 48,
            paddingRight: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              overflow: "hidden",
              "& img": {
                width: "100%",
                height: "100%",
                objectFit: "cover",
              },
              "& svg": {
                width: "100%",
                height: "100%",
              },
            }}
          >
            {item.icon}
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: "0.95rem",
            fontWeight: 500,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const Drawer: React.FC<DrawerProps> = ({
  items,
  position = "bottom",
  open,
  onClose,
  onOpen,
  onReorder,
}) => {
  const anchor = position as "left" | "right" | "bottom";
  const isVertical = position === "left" || position === "right";

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder?.(newItems);
    }
  };

  const getDrawerStyles = () => {
    const baseStyles = {
      "& .MuiDrawer-paper": {
        backgroundColor: (theme: any) =>
          theme.palette.mode === "dark"
            ? "rgba(18, 18, 18, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(20px)",
        borderRadius: position === "bottom" ? "16px 16px 0 0" : "0",
        border: (theme: any) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
        boxShadow: (theme: any) =>
          theme.palette.mode === "dark"
            ? "0 -4px 32px rgba(0, 0, 0, 0.5)"
            : "0 -4px 32px rgba(0, 0, 0, 0.15)",
      },
    };

    switch (position) {
      case "left":
      case "right":
        return {
          ...baseStyles,
          "& .MuiDrawer-paper": {
            ...baseStyles["& .MuiDrawer-paper"],
            width: 280,
            maxHeight: "100vh",
          },
        };
      case "bottom":
      default:
        return {
          ...baseStyles,
          "& .MuiDrawer-paper": {
            ...baseStyles["& .MuiDrawer-paper"],
            maxHeight: "60vh",
          },
        };
    }
  };

  // Get tab handle position based on drawer position
  const getTabHandleStyles = () => {
    const baseStyles = {
      position: "fixed" as const,
      backgroundColor: (theme: any) =>
        theme.palette.mode === "dark"
          ? "rgba(18, 18, 18, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(20px)",
      border: (theme: any) =>
        theme.palette.mode === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      boxShadow: (theme: any) =>
        theme.palette.mode === "dark"
          ? "0 2px 16px rgba(0, 0, 0, 0.5)"
          : "0 2px 16px rgba(0, 0, 0, 0.15)",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      zIndex: 1200,
      "&:hover": {
        backgroundColor: (theme: any) =>
          theme.palette.mode === "dark"
            ? "rgba(30, 30, 30, 0.95)"
            : "rgba(245, 245, 245, 0.95)",
      },
    };

    switch (position) {
      case "left":
        return {
          ...baseStyles,
          top: "50%",
          left: open ? 280 : 0,
          transform: "translateY(-50%)",
          width: 48,
          height: 160,
          borderRadius: "0 12px 12px 0",
          borderLeft: "none",
          writingMode: "vertical-rl" as const,
          textOrientation: "mixed" as const,
        };
      case "right":
        return {
          ...baseStyles,
          top: "50%",
          right: open ? 280 : 0,
          transform: "translateY(-50%)",
          width: 48,
          height: 160,
          borderRadius: "12px 0 0 12px",
          borderRight: "none",
          writingMode: "vertical-rl" as const,
          textOrientation: "mixed" as const,
        };
      case "bottom":
      default:
        return {
          ...baseStyles,
          bottom: open ? "60vh" : 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 160,
          height: 48,
          borderRadius: "12px 12px 0 0",
          borderBottom: "none",
        };
    }
  };

  return (
    <>
      {/* Tab Handle */}
      <Box data-disco="drawer-handle" onClick={open ? onClose : onOpen} sx={getTabHandleStyles()}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            userSelect: "none",
            letterSpacing: isVertical ? "0.1em" : "0.05em",
          }}
        >
          Quick Links
        </Typography>
      </Box>

      {/* Drawer */}
      <MuiDrawer
        anchor={anchor}
        open={open}
        onClose={onClose}
        sx={getDrawerStyles()}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: "primary.main",
            }}
          >
            Quick Links
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {items.map((item) => (
                  <SortableDrawerItem
                    key={item.id}
                    item={item}
                    onClose={onClose}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </List>
        </Box>
      </MuiDrawer>
    </>
  );
};

export default Drawer;
